import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { tenantUsersApi, type TenantUser, type UserAgentAccessAgent } from "@/api/tenant-users";
import { agentAccessApi } from "@/api/agent-access";
import { entraSyncApi, type EntraDirectoryUser } from "@/api/entra-sync";
import { queryKeys } from "@/lib/query-keys";
import {
  Users,
  Plus,
  Trash2,
  Download,
  Shield,
  Eye,
  Loader2,
  Check,
  X,
  Search,
  Bot,
  ChevronDown,
  ChevronRight,
  Layers,
  UsersRound,
} from "lucide-react";

const roleVariant: Record<string, "success" | "warning" | "default" | "secondary"> = {
  tenant_admin: "success",
  agent_admin: "warning",
  standard_user: "secondary",
  // Legacy values — show until migration 0056 has fully propagated.
  admin: "success",
  editor: "warning",
  viewer: "secondary",
};

function roleLabel(role: string): string {
  switch (role) {
    case "tenant_admin": case "admin": return "Tenant admin";
    case "agent_admin": case "editor": return "Agent admin";
    case "standard_user": case "viewer": return "Standard user";
    default: return role;
  }
}

export function UserManagement() {
  const { tenantId, isAdmin } = useTenant();
  const queryClient = useQueryClient();
  const [showImport, setShowImport] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [importSearch, setImportSearch] = useState("");
  const [selectedImports, setSelectedImports] = useState<Set<string>>(new Set());
  // Filters the tenant's own user list. Separate from `importSearch`, which
  // searches the Entra directory in the import panel.
  const [search, setSearch] = useState("");

  // New user form
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("standard_user");
  // Which user the delete confirmation is open for (null = closed). An in-app
  // dialog rather than window.confirm so it matches the rest of the app.
  const [confirmUser, setConfirmUser] = useState<{ id: string; label: string } | null>(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: queryKeys.tenantUsers.byCompany(tenantId),
    queryFn: () => tenantUsersApi.list(tenantId),
  });

  const {
    data: entraUsers = [],
    isLoading: entraLoading,
    refetch: refetchEntra,
  } = useQuery({
    queryKey: queryKeys.entra.users(tenantId),
    queryFn: () => entraSyncApi.listUsers(tenantId),
    enabled: showImport,
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => tenantUsersApi.delete(tenantId, userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.tenantUsers.byCompany(tenantId) }),
  });

  const roleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      tenantUsersApi.update(tenantId, userId, { role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.tenantUsers.byCompany(tenantId) }),
  });

  const importMutation = useMutation({
    mutationFn: (selected: EntraDirectoryUser[]) =>
      entraSyncApi.importUsers(
        tenantId,
        selected.map((u) => ({
          externalId: u.externalId,
          email: u.email,
          displayName: u.displayName,
        })),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantUsers.byCompany(tenantId) });
      setShowImport(false);
      setSelectedImports(new Set());
    },
  });

  const addUserMutation = useMutation({
    mutationFn: () =>
      tenantUsersApi.create(tenantId, {
        email: newEmail,
        displayName: newName || undefined,
        role: newRole,
        authProvider: "local",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantUsers.byCompany(tenantId) });
      setShowAddUser(false);
      setNewEmail("");
      setNewName("");
      setNewRole("standard_user");
    },
  });

  // Name, email and the role as it is written on screen, so typing "agent
  // admin" finds them rather than only matching the stored `agent_admin`.
  const query = search.trim().toLowerCase();
  const filteredUsers = query
    ? users.filter((u) =>
        [u.displayName ?? "", u.email, roleLabel(u.role)].some((field) =>
          field.toLowerCase().includes(query),
        ),
      )
    : users;

  const existingExternalIds = new Set(users.map((u) => u.externalId).filter(Boolean));
  const filteredEntra = entraUsers.filter(
    (u) =>
      !existingExternalIds.has(u.externalId) &&
      (u.displayName.toLowerCase().includes(importSearch.toLowerCase()) ||
        u.email.toLowerCase().includes(importSearch.toLowerCase())),
  );

  const toggleImport = (externalId: string) => {
    setSelectedImports((prev) => {
      const next = new Set(prev);
      if (next.has(externalId)) next.delete(externalId);
      else next.add(externalId);
      return next;
    });
  };

  const handleImport = () => {
    const selected = entraUsers.filter((u) => selectedImports.has(u.externalId));
    if (selected.length > 0) importMutation.mutate(selected);
  };

  return (
    <CenterPanel title="User Management">
      {/* Search on the left, then the count sitting with the buttons it
          belongs beside. The count reads "2 of 12" while filtering, so it
          never claims a total the list below is not showing. */}
      <div className="mb-4 flex items-center gap-3">
        {users.length > 0 && (
          <div className="relative max-w-xs flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-border bg-surface pl-8 pr-3 py-2 text-sm"
            />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <p className="shrink-0 text-sm text-text-secondary">
            {query
              ? `${filteredUsers.length} of ${users.length}`
              : `${users.length} user${users.length !== 1 ? "s" : ""}`}
          </p>
          {isAdmin && (
            <>
              <Button size="sm" variant="outline" onClick={() => setShowImport(!showImport)}>
                <Download size={16} className="mr-1.5" />
                Import from Entra
              </Button>
              <Button size="sm" onClick={() => setShowAddUser(!showAddUser)}>
                <Plus size={16} className="mr-1.5" />
                Add User
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Add User Form */}
      {showAddUser && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-sm">Add User</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <input
                  type="email"
                  placeholder="Email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  placeholder="Display Name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm"
                />
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
                >
                  <option value="standard_user">Standard user</option>
                  <option value="agent_admin">Agent admin</option>
                  <option value="tenant_admin">Tenant admin</option>
                </select>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => addUserMutation.mutate()} disabled={!newEmail}>
                  Add
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowAddUser(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entra Import Panel */}
      {showImport && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Download size={16} />
              Import Users from Entra ID
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-3 flex items-center gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search directory users..."
                  value={importSearch}
                  onChange={(e) => setImportSearch(e.target.value)}
                  className="w-full rounded-md border border-border bg-surface pl-8 pr-3 py-2 text-sm"
                />
              </div>
              {selectedImports.size > 0 && (
                <Button size="sm" onClick={handleImport} disabled={importMutation.isPending}>
                  {importMutation.isPending ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Check size={14} className="mr-1.5" />
                  )}
                  Import {selectedImports.size} user{selectedImports.size !== 1 ? "s" : ""}
                </Button>
              )}
            </div>

            {entraLoading ? (
              <div className="flex items-center gap-2 py-4 text-sm text-text-muted">
                <Loader2 size={14} className="animate-spin" />
                Loading directory...
              </div>
            ) : filteredEntra.length === 0 ? (
              <p className="py-4 text-sm text-text-muted">
                {entraUsers.length === 0
                  ? "No users found in Entra directory. Ensure admin consent has been granted."
                  : "All directory users have been imported, or no matches found."}
              </p>
            ) : (
              <div className="max-h-[300px] overflow-y-auto">
                {filteredEntra.map((user) => (
                  <label
                    key={user.externalId}
                    className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 hover:bg-surface-hover"
                  >
                    <input
                      type="checkbox"
                      checked={selectedImports.has(user.externalId)}
                      onChange={() => toggleImport(user.externalId)}
                      className="rounded border-border"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-text-primary">{user.displayName}</p>
                      <p className="text-xs text-text-muted">{user.email}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* User List */}
      {isLoading ? (
        <p className="text-sm text-text-muted">Loading users...</p>
      ) : users.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Users size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">No users yet.</p>
            <p className="text-xs text-text-muted mt-1">
              Import users from Entra ID or add them manually.
            </p>
          </CardContent>
        </Card>
      ) : filteredUsers.length === 0 ? (
        /* A search that found nothing is not the same as having no users.
           Kept separate so the empty-state copy above never lies. */
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Users size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">No users match &ldquo;{search.trim()}&rdquo;.</p>
            <button
              type="button"
              onClick={() => setSearch("")}
              className="mt-2 text-xs text-accent hover:underline"
            >
              Clear search
            </button>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {filteredUsers.map((user) => (
            <UserRow
              key={user.id}
              user={user}
              tenantId={tenantId}
              isAdmin={isAdmin}
              onRoleChange={(role) => roleMutation.mutate({ userId: user.id, role })}
              onDelete={() =>
                setConfirmUser({ id: user.id, label: user.displayName || user.email })
              }
            />
          ))}
        </div>
      )}

      {/* Delete confirmation — in-app dialog (matches the session-delete modal). */}
      {confirmUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setConfirmUser(null)}
        >
          <div
            className="w-[min(380px,calc(100vw-2rem))] rounded-lg border border-border bg-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-sm font-semibold text-text-primary">Remove user</h3>
            <p className="mb-4 text-xs text-text-secondary">
              Remove{" "}
              <span className="font-medium text-text-primary">{confirmUser.label}</span>{" "}
              from this tenant? This revokes their access to ProClaw and cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setConfirmUser(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => {
                  deleteMutation.mutate(confirmUser.id);
                  setConfirmUser(null);
                }}
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}
    </CenterPanel>
  );
}

// ---------------------------------------------------------------------------
// UserRow — collapsed view shows identity / role / access count, with a
// chevron that expands an inline per-agent access table. Tenant admins
// implicitly have access to every agent so we render a read-only banner
// for those users instead of checkboxes. Agent admins and standard users
// get checkboxes that grant/revoke direct agent_access rows; group-
// mediated access is surfaced as a read-only "via Group X" label.
// ---------------------------------------------------------------------------

interface UserRowProps {
  user: TenantUser;
  tenantId: string;
  isAdmin: boolean;
  onRoleChange: (role: string) => void;
  onDelete: () => void;
}

function UserRow({ user, tenantId, isAdmin, onRoleChange, onDelete }: UserRowProps) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const isTenantAdminRow = user.role === "tenant_admin" || user.role === "admin";

  // Fetch the per-user access view only after first expand to avoid N
  // requests on every page load. Staleness is fine because the user
  // can re-toggle to refetch.
  const accessQueryKey = ["user-agent-access", tenantId, user.id] as const;
  const { data: access, isLoading: accessLoading, error: accessError } = useQuery({
    queryKey: accessQueryKey,
    queryFn: () => tenantUsersApi.agentAccess(tenantId, user.id),
    enabled: expanded && isAdmin,
    retry: false,
  });

  const grantMutation = useMutation({
    mutationFn: (agentId: string) =>
      agentAccessApi.grant(tenantId, agentId, { tenantUserId: user.id }),
    // Optimistic patch keeps the checkbox tick instant. Always refetch on
    // settle so we pick up the real access-row id (needed for revoke).
    onMutate: async (agentId) => {
      await queryClient.cancelQueries({ queryKey: accessQueryKey });
      const prev = queryClient.getQueryData(accessQueryKey);
      queryClient.setQueryData(accessQueryKey, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          agents: old.agents.map((a: UserAgentAccessAgent) =>
            a.agentId === agentId
              ? { ...a, directAccessId: "__pending__", hasAccess: true }
              : a,
          ),
        };
      });
      return { prev };
    },
    onError: (_e, _agentId, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(accessQueryKey, ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: accessQueryKey });
      // Other surfaces (Agents page, accessible-agents) need to refetch too.
      queryClient.invalidateQueries({ queryKey: ["accessible-agents", tenantId] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: ({ agentId, accessId }: { agentId: string; accessId: string }) =>
      agentAccessApi.revoke(tenantId, agentId, accessId),
    onMutate: async ({ agentId }) => {
      await queryClient.cancelQueries({ queryKey: accessQueryKey });
      const prev = queryClient.getQueryData(accessQueryKey);
      queryClient.setQueryData(accessQueryKey, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          agents: old.agents.map((a: UserAgentAccessAgent) =>
            a.agentId === agentId
              ? { ...a, directAccessId: null, hasAccess: a.viaGroups.length > 0 }
              : a,
          ),
        };
      });
      return { prev };
    },
    onError: (_e, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(accessQueryKey, ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: accessQueryKey });
      queryClient.invalidateQueries({ queryKey: ["accessible-agents", tenantId] });
    },
  });

  const handleToggle = (agent: UserAgentAccessAgent) => {
    if (agent.directAccessId && agent.directAccessId !== "__pending__") {
      revokeMutation.mutate({ agentId: agent.agentId, accessId: agent.directAccessId });
    } else if (!agent.directAccessId) {
      grantMutation.mutate(agent.agentId);
    }
  };

  // Access count for the collapsed pill — direct OR via groups counts as
  // visible access. For tenant admins it's "all" since they bypass grants.
  const accessCount = access?.agents.filter((a) => a.hasAccess).length;
  const totalAgents = access?.agents.length;

  return (
    <Card>
      <CardContent className="py-3">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="flex flex-1 items-center gap-3 text-left"
            aria-expanded={expanded}
          >
            {expanded ? (
              <ChevronDown size={14} className="text-text-muted shrink-0" />
            ) : (
              <ChevronRight size={14} className="text-text-muted shrink-0" />
            )}
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/10">
              <span className="text-sm font-medium text-accent">
                {(user.displayName || user.email)[0]?.toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-text-primary">
                {user.displayName || user.email}
              </p>
              <p className="truncate text-xs text-text-muted">
                {user.email}
                {user.authProvider && (
                  <span className="ml-2 text-text-muted">via {user.authProvider}</span>
                )}
              </p>
            </div>
          </button>

          <div className="flex items-center gap-2 shrink-0">
            {/* Compact access count — clarifies why the row is interesting
                without forcing the user to expand it. Tenant admins skip
                the count since their access is implicit. */}
            {!isTenantAdminRow && (
              <Badge
                variant={accessCount && accessCount > 0 ? "default" : "secondary"}
                className="hidden sm:inline-flex"
              >
                <Bot size={11} className="mr-1" />
                {accessCount === undefined && totalAgents === undefined
                  ? "Agents"
                  : `${accessCount ?? 0} / ${totalAgents ?? 0}`}
              </Badge>
            )}
            {isAdmin ? (
              <select
                value={user.role}
                onChange={(e) => onRoleChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="rounded-md border border-border bg-surface px-2 py-1 text-xs"
                title="Tenant admin: full access. Agent admin: manages assigned agents + catalogues. Standard user: chat only."
              >
                <option value="tenant_admin">Tenant admin</option>
                <option value="agent_admin">Agent admin</option>
                <option value="standard_user">Standard user</option>
              </select>
            ) : (
              <Badge variant={roleVariant[user.role] ?? "default"}>
                {isTenantAdminRow ? (
                  <><Shield size={12} className="mr-1" />Tenant admin</>
                ) : user.role === "agent_admin" || user.role === "editor" ? (
                  <><Shield size={12} className="mr-1" />Agent admin</>
                ) : (
                  <><Eye size={12} className="mr-1" />Standard user</>
                )}
              </Badge>
            )}
            {isAdmin && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
              >
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        </div>

        {expanded && (
          <div className="mt-3 border-t border-border pt-3">
            {isTenantAdminRow ? (
              <div className="flex items-start gap-2 rounded-md bg-accent/5 px-3 py-2 text-xs text-text-secondary">
                <Shield size={14} className="mt-0.5 shrink-0 text-accent" />
                <span>
                  Tenant admins automatically have access to every agent. To restrict
                  this user, change their role to <strong>Agent admin</strong> or{" "}
                  <strong>Standard user</strong> first.
                </span>
              </div>
            ) : accessLoading ? (
              <p className="text-xs text-text-muted">
                <Loader2 size={12} className="mr-1 inline animate-spin" />
                Loading agent access...
              </p>
            ) : accessError ? (
              <p className="text-xs text-danger">
                Could not load agent list:{" "}
                {accessError instanceof Error ? accessError.message : "unknown error"}
              </p>
            ) : !access || access.agents.length === 0 ? (
              <p className="text-xs text-text-muted">
                No agents in this tenant yet. Deploy an agent first, then come
                back here to grant access.
              </p>
            ) : (
              <div className="flex flex-col gap-1">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                  Tick the agents this user should have access to
                </p>
                {access.agents.map((agent) => (
                  <AgentAccessCheckbox
                    key={agent.agentId}
                    agent={agent}
                    onToggle={() => handleToggle(agent)}
                    pending={
                      grantMutation.isPending || revokeMutation.isPending
                    }
                  />
                ))}
                <p className="mt-2 text-[11px] text-text-muted">
                  Checkboxes control <em>direct</em> grants. Access via a group is
                  managed in <strong>Groups</strong>.
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AgentAccessCheckbox({
  agent,
  onToggle,
  pending,
}: {
  agent: UserAgentAccessAgent;
  onToggle: () => void;
  pending: boolean;
}) {
  const checked = !!agent.directAccessId;
  // A user can have access via a group without a direct grant; we keep
  // the checkbox unchecked (because there's no direct row to toggle) but
  // surface the source so admins aren't surprised.
  const groupOnly = !checked && agent.viaGroups.length > 0;
  return (
    <label
      className={`flex items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors ${
        pending ? "opacity-60" : "hover:bg-surface-hover cursor-pointer"
      }`}
    >
      <span className="flex items-center gap-2 min-w-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          disabled={pending || agent.directAccessId === "__pending__"}
          className="h-3.5 w-3.5 rounded border-border"
        />
        {agent.isSubagent ? (
          <Layers size={12} className="text-purple-500 shrink-0" />
        ) : (
          <Bot size={12} className="text-accent shrink-0" />
        )}
        <span className="truncate text-text-primary">{agent.name}</span>
        {agent.isSubagent && (
          <span className="text-[10px] text-text-muted shrink-0">(subagent)</span>
        )}
      </span>
      {groupOnly && (
        <span className="ml-2 flex items-center gap-1 text-[10px] text-text-muted shrink-0">
          <UsersRound size={10} />
          via {agent.viaGroups.map((g) => g.groupName).join(", ")}
        </span>
      )}
    </label>
  );
}
